import { Mail } from "lucide-react";
import { useState } from "react";
import { NotificationTemplateEditor } from "@/components/settings/NotificationTemplateEditor";
import { ReactivateDialog } from "@/components/settings/ReactivateDialog";
import { TestEmailNotice } from "@/components/settings/TestEmailNotice";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  type EmailTemplateRow,
  useEmailTemplates,
  useSetTemplateActive,
} from "@/hooks/use-email-templates";
import { useSession } from "@/lib/auth-client";
import { formatDateTime } from "@/lib/dates";
import { sameTemplateContent } from "@/lib/email-template-content";
import { canEditAll } from "@/lib/permissions";
import { cn } from "@/lib/utils";

/**
 * Settings → Config → Notification Templates: the emails the review process
 * sends, each rewordable and each switchable off. Switching one off is
 * immediate; switching it back on asks what to do about anyone who became due
 * it in the meantime.
 */
export function NotificationTemplates() {
  const { data: rows = [], isLoading, isError, error, refetch } = useEmailTemplates();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load notification templates"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-muted-foreground text-sm">
        These are the emails the review process sends. Edit the wording, or switch one off to stop
        it being sent.
      </p>

      <TestEmailNotice context="templates" />

      <DataTable
        headers={[
          { label: "Email" },
          { label: "Sent when" },
          { label: "On" },
          { label: "Last edited" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Mail}
            title="No notification templates"
            description="The review emails will appear here once the server has registered them."
          />
        }
      >
        {rows.map((template) => (
          <TemplateRow key={template.key} template={template} />
        ))}
      </DataTable>
    </div>
  );
}

/** One email: owns its own switch mutation and its two dialogs. */
function TemplateRow({ template }: { template: EmailTemplateRow }) {
  const setActive = useSetTemplateActive(template.key);
  const [editing, setEditing] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const off = !template.isActive;
  const { data: session } = useSession();
  const canEdit = canEditAll(session?.user?.role);
  const isDefaultWording = sameTemplateContent(template.content, template.defaultContent);

  function onToggle(next: boolean) {
    if (next) setReactivating(true);
    else setActive.mutate({ active: false });
  }

  return (
    <tr
      className={cn(
        "border-b border-border last:border-0 hover:bg-muted/30 transition-colors",
        off && "text-muted-foreground",
      )}
    >
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("font-semibold", !off && "text-foreground")}>{template.label}</span>
          {off && <Badge variant="secondary">Off</Badge>}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={cn(!off && "text-muted-foreground")}>{template.description ?? "—"}</span>
      </td>
      <td className="px-4 py-3">
        <Checkbox
          checked={template.isActive}
          disabled={!canEdit || setActive.isPending}
          aria-label={`${template.label} is on`}
          onCheckedChange={(next) => onToggle(next === true)}
        />
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {template.updatedAt && !isDefaultWording ? (
          <>
            <span className="block">{template.updatedBy ?? "—"}</span>
            <span className="block text-xs">{formatDateTime(template.updatedAt)}</span>
          </>
        ) : (
          "Default wording"
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {/* Mounted only while open, so each open starts from the saved state. */}
        {editing && (
          <NotificationTemplateEditor
            key={template.key}
            template={template}
            open={editing}
            onOpenChange={setEditing}
          />
        )}
        {reactivating && (
          <ReactivateDialog
            template={template}
            open={reactivating}
            onOpenChange={setReactivating}
          />
        )}
      </td>
    </tr>
  );
}
